import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/useAppDispatch';
import { setToken } from '../store/authSlice';
import { hasCentralAdminClaims } from '../utils/adminAccess';

export function LoginPage() {
  const [tokenInput, setTokenInput] = useState('');
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();
  const { isDevMode } = useAppSelector((state) => state.auth);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedToken = tokenInput.trim();
    if (!trimmedToken) {
      setError('Please enter a JWT token');
      return;
    }

    // Basic JWT format validation
    const parts = trimmedToken.split('.');
    if (parts.length !== 3) {
      setError('Invalid JWT token format');
      return;
    }

    // The console only renders for admin-claimed tokens (App ProtectedRoutes),
    // so accepting any other token would silently bounce to the user area.
    // Say why instead. The server still verifies the token on every request.
    if (!hasCentralAdminClaims(trimmedToken)) {
      setError(
        'This token has no Central admin claim (centralAdmin: true, isAdmin: true, or roles: ["central_admin"]).',
      );
      return;
    }

    dispatch(setToken(trimmedToken));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      {isDevMode && (
        <div className="bg-amber-500 text-amber-900 text-center py-2 px-4 rounded-t-lg text-sm font-medium w-full max-w-md">
          DEV AUTH MODE - Do not use in production
        </div>
      )}

      <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          LevelUp Central Admin
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Paste your admin JWT token to continue
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="token"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              JWT Token
            </label>
            <textarea
              id="token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 transition-colors"
          >
            Login
          </button>
        </form>

        <div className="mt-6 p-4 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-600 font-medium mb-2">
            Required JWT Claims:
          </p>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>
              <code className="bg-gray-100 px-1 rounded">userId</code> - Required
            </li>
            <li>
              <code className="bg-gray-100 px-1 rounded">centralAdmin: true</code> - OR
            </li>
            <li>
              <code className="bg-gray-100 px-1 rounded">isAdmin: true</code> - OR
            </li>
            <li>
              <code className="bg-gray-100 px-1 rounded">roles: ["central_admin"]</code>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
